import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { SubHeader } from "../../Common/layout/SubHeader";
import ReactApexChart from "react-apexcharts";
import {
    useGetGemsAccrualRedemptionTotalsQuery,
    useGetGemsExchangeRateActiveQuery,
} from "../../../../redux/apiSlice";
import { formatStatus } from "../../../util/Helpers";

const todayStr = new Date().toISOString().split("T")[0];

const formatMoney = (currency, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "—";
    return `${currency || "NGN"} ${numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const GemsAccruals = () => {
    const { userGroup } = useParams();

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: todayStr,
    });
    const [conversionGems, setConversionGems] = useState("");

    const commonArgs = useMemo(
        () => ({
            userGroup,
            startDate: filters.startDate || "",
            endDate: filters.endDate || "",
        }),
        [userGroup, filters.startDate, filters.endDate]
    );

    const { data: accrualRedemptionTotals, isFetching: isFetchingAccrualRedemptionTotals } =
        useGetGemsAccrualRedemptionTotalsQuery(commonArgs);
    const { data: activeExchangeRate, isFetching: isFetchingActiveExchangeRate } = useGetGemsExchangeRateActiveQuery(
        { userGroup }
    );
    const activeRate = activeExchangeRate?.data || activeExchangeRate || null;
    const activeRateValue = Number(activeRate?.rate ?? 0);
    const activeRateCurrency = activeRate?.currency || "NGN";
    const gemsToCash = (gems) => {
        const numericGems = Number(gems);
        if (!Number.isFinite(numericGems) || !Number.isFinite(activeRateValue) || activeRateValue <= 0) return null;
        return numericGems * activeRateValue;
    };

    const accrualTotals = accrualRedemptionTotals?.data || accrualRedemptionTotals || null;
    const accrualBreakdown = useMemo(
        () =>
            (Array.isArray(accrualTotals?.breakdown) ? accrualTotals.breakdown : []).filter(
                (entry) => entry?.isRedemption !== true
            ),
        [accrualTotals?.breakdown]
    );
    const accrualOverall = accrualTotals?.overall || null;

    const accrualChartSeries = useMemo(
        () => accrualBreakdown.map((b) => Number(b.totalAccrued ?? 0)),
        [accrualBreakdown]
    );
    const accrualChartLabels = useMemo(
        () => accrualBreakdown.map((b) => b.rewardTypeLabel || b.rewardType),
        [accrualBreakdown]
    );
    const hasAccrualChartData = accrualChartSeries.some((v) => v > 0);
    const accrualChartOptions = useMemo(
        () => ({
            labels: accrualChartLabels,
            legend: { position: "bottom" },
            dataLabels: { enabled: false },
            colors: ["#6993FF", "#1BC5BD", "#FFA800", "#F64E60", "#8950FC", "#00A3FF"],
        }),
        [accrualChartLabels]
    );

    const onFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const totalAccrued = Number(accrualOverall?.totalAccrued ?? 0);
    const totalAccruedValue = gemsToCash(totalAccrued);
    const conversionCashValue = gemsToCash(conversionGems);

    return (
        <>
            {userGroup && (
                <SubHeader>
                    <SubHeader.Nav>
                        <SubHeader.NavItem label={"Overview"} to={`/gems/${userGroup}/overview`} />
                        <SubHeader.NavItem label={"Accruals"} to={`/gems/${userGroup}/overview/accruals`} />
                        <SubHeader.NavItem label={"Redemptions"} to={`/gems/${userGroup}/overview/redemptions`} />
                        <SubHeader.NavItem label={"Campaigns"} to={`/gems/${userGroup}/overview/campaigns`} />
                        <SubHeader.NavItem label={"Reward attempts"} to={`/gems/${userGroup}/overview/reward-attempts`} />
                        <SubHeader.NavItem label={"Audit logs"} to={`/gems/${userGroup}/overview/audit-logs`} />
                    </SubHeader.Nav>
                </SubHeader>
            )}
            <div className="py-4 px-6">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="font-weight-bolder mb-1">Gems accruals</h2>
                        <span className="text-muted">Accrual totals and breakdown for the selected period.</span>
                        <div className="d-flex flex-wrap mt-4">
                            <div className="mr-3 mb-2">
                                <label className="text-muted font-size-sm mb-1">Start date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="startDate"
                                    value={filters.startDate}
                                    onChange={onFilterChange}
                                />
                            </div>
                            <div className="mb-2">
                                <label className="text-muted font-size-sm mb-1">End date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="endDate"
                                    value={filters.endDate}
                                    onChange={onFilterChange}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="text-right" style={{ marginRight: "4rem" }}>
                        {isFetchingActiveExchangeRate ? (
                            <div className="text-muted">Loading rate...</div>
                        ) : activeRate ? (
                            <div className="d-flex flex-column align-items-end">
                                <span className="text-muted font-size-sm">Active exchange rate</span>
                                <span className="font-weight-bolder">
                                    1 Gem = {Number(activeRate.rate ?? 0).toLocaleString()} {activeRate.currency || "—"}
                                </span>
                                <span className="text-muted font-size-sm">{formatStatus(activeRate.status || "active")}</span>
                                <div className="d-flex align-items-center mt-3">
                                    <input
                                        type="number"
                                        className="form-control form-control-sm mr-2"
                                        value={conversionGems}
                                        onChange={(e) => setConversionGems(e.target.value)}
                                        placeholder="Gems"
                                        style={{ width: 110 }}
                                        min="0"
                                    />
                                    <span className="text-muted font-size-sm">
                                        {conversionGems
                                            ? conversionCashValue !== null
                                                ? `≈ ${formatMoney(activeRateCurrency, conversionCashValue)}`
                                                : "—"
                                            : `Convert to ${activeRateCurrency}`}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="text-muted">No active exchange rate</div>
                        )}
                    </div>
                </div>

                <div className="row mb-6">
                    <div className="col-md-4 mb-4">
                        <Card>
                            <CardBody>
                                <div className="d-flex flex-column">
                                    <span className="text-muted font-size-sm mb-2">Total accrued</span>
                                    <span className="font-weight-bolder font-size-h3 text-primary">
                                        {totalAccrued.toLocaleString()}
                                    </span>
                                    <span className="text-muted font-size-sm">
                                        {totalAccruedValue !== null ? `≈ ${formatMoney(activeRateCurrency, totalAccruedValue)}` : "—"}
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    <div className="col-md-4 mb-4">
                        <Card>
                            <CardBody>
                                <div className="d-flex flex-column">
                                    <span className="text-muted font-size-sm mb-2">Transactions</span>
                                    <span className="font-weight-bolder font-size-h3">
                                        {Number(accrualOverall?.totalTransactions ?? 0).toLocaleString()}
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    <div className="col-md-4 mb-4">
                        <Card>
                            <CardBody>
                                <div className="d-flex flex-column">
                                    <span className="text-muted font-size-sm mb-2">Unique users</span>
                                    <span className="font-weight-bolder font-size-h3">
                                        {Number(accrualOverall?.uniqueUsers ?? 0).toLocaleString()}
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>

                <div className="row mb-6">
                    <div className="col-lg-5 mb-6 mb-lg-0">
                        <Card>
                            <CardHeader title="Accrual breakdown" />
                            <CardBody>
                                {isFetchingAccrualRedemptionTotals ? (
                                    <div className="text-center text-muted py-5">Loading...</div>
                                ) : hasAccrualChartData ? (
                                    <ReactApexChart options={accrualChartOptions} series={accrualChartSeries} type="donut" height={300} />
                                ) : (
                                    <div className="text-center text-muted py-5">No accrual data for selected period</div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                    <div className="col-lg-7">
                        <Card>
                            <CardHeader title="Accrual table" />
                            <CardBody>
                                <div className="table-responsive">
                                    <table className="table table-sm table-head-custom">
                                        <thead>
                                            <tr>
                                                <th>Reward type</th>
                                                <th className="text-right">Total accrued</th>
                                                <th className="text-right">Value</th>
                                                <th className="text-right">Transactions</th>
                                                <th className="text-right">Unique users</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isFetchingAccrualRedemptionTotals ? (
                                                <tr>
                                                    <td colSpan={5} className="text-center text-muted py-5">
                                                        Loading...
                                                    </td>
                                                </tr>
                                            ) : accrualBreakdown.length ? (
                                                accrualBreakdown.map((b) => {
                                                    const accrued = Number(b.totalAccrued ?? 0);
                                                    const cashValue = gemsToCash(accrued);
                                                    return (
                                                        <tr key={b.rewardType}>
                                                            <td>{b.rewardTypeLabel || b.rewardType}</td>
                                                            <td className="text-right font-weight-bolder">{accrued.toLocaleString()}</td>
                                                            <td className="text-right text-muted">
                                                                {cashValue !== null ? formatMoney(activeRateCurrency, cashValue) : "—"}
                                                            </td>
                                                            <td className="text-right">{Number(b.transactionCount ?? 0).toLocaleString()}</td>
                                                            <td className="text-right">{Number(b.uniqueUsers ?? 0).toLocaleString()}</td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="text-center text-muted py-5">
                                                        No breakdown data
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>
        </>
    );
};

export default GemsAccruals;
